// SyndicationInvite — landing page for `/syndication/invite/:token`.
//
// Unauthed visitors get bounced to /login?next=... per the new model
// (legacy magic-link unauth view was dropped). Authed visitors see a card
// summarising the partnership they're being invited to and an Accept button
// that calls POST /api/syndication/invites/accept.

import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";

export default function SyndicationInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = encodeURIComponent(location.pathname);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [authLoading, user, navigate, location.pathname]);

  if (authLoading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/syndication/invites/accept", { invite_token: token });
      setAccepted(data);
      toast.success("You're in. Welcome to the syndication.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept the invite");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-xl px-4 py-12">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <Users className="h-6 w-6" />
          </div>
          {accepted ? (
            <div data-testid="synd-accept-success">
              <h1 className="text-2xl font-semibold text-slate-900">
                You're in.
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Welcome to <strong>{accepted.partnership?.name}</strong>. The
                sponsor can now see you in the cap table.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="mt-6 bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Go to dashboard
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-slate-900">
                You've been invited to a syndication.
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Click below to accept and join the cap table. You can review
                the partnership details after accepting.
              </p>
              <Button
                onClick={handleAccept}
                disabled={accepting}
                data-testid="synd-accept-btn"
                className="mt-6 bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
              >
                {accepting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accepting…</>
                ) : (
                  <><CheckCircle2 className="mr-2 h-4 w-4" /> Accept invitation</>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
